import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, CalendarDays, Clock } from "lucide-react";
import PageHero from "@/components/PageHero";
import SEO, { buildBreadcrumbJsonLd } from "@/components/SEO";
import { seoConfig } from "@/data/seoConfig";
import { blogPosts } from "@/data/blogPosts";

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

const BlogListing = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO {...seoConfig.blogListing} jsonLd={[buildBreadcrumbJsonLd(seoConfig.blogListing.crumbs)]} />
      <PageHero title="Insights & Articles" crumbs={[{ label: "Home", href: "/" }, { label: "Blog" }]} lightfall />

      <section data-testid="blog-listing-section" className="py-24 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">
              Articles & Case Studies
            </span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900">
              Insights on AI, Transformation, and Data
            </h2>
            <p className="mt-4 text-base md:text-lg text-slate-500">
              Practical, no-hype guides from our team on generative AI, digital transformation, and data
              intelligence — grounded in real enterprise engagements.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5">
            {blogPosts.map((post, i) => (
              <motion.a
                key={post.slug}
                href={`/blog/${post.slug}`}
                data-testid={`blog-listing-card-${post.slug}`}
                {...fadeUp}
                transition={{ duration: 0.65, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -6 }}
                className="group rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-miti-blue/40 hover:shadow-xl hover:shadow-miti-blue/5 transition-all duration-300"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={post.heroImage}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 rounded-lg bg-white/95 backdrop-blur px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-miti-blue shadow">
                    {post.category}
                  </div>
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span className="flex items-center gap-1.5">
                      <CalendarDays size={13} /> {formatDate(post.date)}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock size={13} /> {post.readTime}
                    </span>
                  </div>
                  <h3 className="mt-3 font-heading font-semibold text-lg text-slate-900 leading-snug group-hover:text-miti-blue transition-colors">
                    {post.title}
                  </h3>
                  <p className="mt-3 text-sm text-slate-500 leading-relaxed">{post.excerpt}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-miti-blue">
                    Read Article
                    <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogListing;
